import { Chip, Stack } from '@mui/material';
import { ExtendedList, NestedList } from '@procureai/ui-mui';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LineItemsQueryDataType } from '../../DemandRecommendationWidget.types';
import { DemandRecommendationFormData } from '../DemandRecommendationForm/DemandRecommendationForm.schema';
import { countDirty, countNonDirty } from '../DemandRecommendationForm/DemandRecommendationForm.util';
import { RecommendationItem } from '../RecommendationItem/RecommendationItem';
import { I18nNamespace } from '~/common/types';

import { AccordionItem } from './AccordionItem';

interface AccordionProps {
  lineItem: LineItemsQueryDataType;
  itemIndex: number;
}

export const Accordion = ({ lineItem, itemIndex }: AccordionProps) => {
  const { watch } = useFormContext<DemandRecommendationFormData>();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandRecommendationWidget' });

  const formRecommendations = watch();

  const recommendations = formRecommendations.demandRequestItems?.[itemIndex]?.recommendations ?? [];
  const appliedCount = countNonDirty(recommendations);
  const selectedCount = countDirty(recommendations);
  const recommendationsCount = lineItem?.recommendations?.length ?? 0;
  const openRecommendations = recommendationsCount - appliedCount;
  const isExpanded = openRecommendations > 0;

  return (
    <AccordionItem
      key={`${lineItem.id}${isExpanded}`}
      title={t('lineItem', { position: lineItem.position, title: lineItem.title })}
      subtitle={lineItem.description ?? undefined}
      isExpanded={isExpanded}
      totalRecommendations={recommendationsCount}
      right={
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: 'center',
          }}
        >
          {appliedCount !== recommendationsCount || !recommendationsCount ? (
            <Chip
              color={'warning'}
              label={t('chips.recommendations', {
                count: openRecommendations,
              })}
            />
          ) : null}
          {appliedCount ? <Chip color="success" label={t('chips.applied', { count: appliedCount })} /> : null}
          {selectedCount ? (
            <Chip
              color="success"
              label={t('chips.selected', {
                count: selectedCount,
              })}
            />
          ) : null}
        </Stack>
      }
    >
      <NestedList>
        <ExtendedList>
          {lineItem?.recommendations?.map((recommendation, index) => (
            <RecommendationItem
              key={recommendation.id}
              isExpanded={isExpanded}
              index={index}
              itemIndex={itemIndex}
              recommendation={recommendation}
            />
          ))}
        </ExtendedList>
      </NestedList>
    </AccordionItem>
  );
};
